import React, { useContext, useEffect, useState } from "react";
import {
    IonLabel,
    IonButton,
    IonItem,
    IonSelect,
    IonSelectOption,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCol,
    IonGrid,
    IonRow,
    IonToolbar,
    IonIcon,
    IonText
} from "@ionic/react";


import { useForm, Controller } from 'react-hook-form';
import { QuestionOption, Section, SectionCondition, Validation, ValidationSearchParams, ValidationToUpdate, } from "models/questionnaire.model";
import { AppContext } from "context/app.context";
import { QuestionnaireActionTypes } from "reducers/questionnaire.reducer";
import { createNewValidation, getValidationList, updateValidation } from "misc/phqHTTP";
import { createOutline, trashOutline } from "ionicons/icons";
import { UIElementEnum } from "models/ui-element.enum";
import { ValidationAppliesToEnum, ValidationTypeEnum } from "models/validation.enum";
import { AuthContext } from "context/auth.context";


export interface ConditionalFormProps {
    Section?: Section
}

export interface ConditionType {
    Value: string;
    Description: string;
}

export interface Action {
    Value: string;
    Description: string;
    Type: string;
}


let initialValues = {
    ActionFieldID: 0,
    ActionType: "",
    ConditionType: "",
    ConditionValue: "",
    QuestionID: 0,
    Priority: 0,
    ConditionID: 0
} as SectionCondition

const actionTypes =
    [{
        Value: 'HideQuestion',
        Description: 'Hide Question',
        Type: 'Question'
    },
    {
        Value: 'ShowQuestion',
        Description: 'Show Question',
        Type: 'Question'
    },
    {
        Value: 'HideSection',
        Description: 'Hide Section',
        Type: 'Section'
    },
    {
        Value: 'ShowSection',
        Description: 'Show Section',
        Type: 'Section'
    },
    {
        Value: 'ContinueTo',
        Description: 'Continue to page',
        Type: 'Page'
    }
    ];

const ConditionalForm: React.FC = () => {
    const { handleSubmit, control, reset, watch } = useForm<SectionCondition>({
        defaultValues: { ...initialValues }
    }
    );
    const appContext = useContext(AppContext);
    const { logInfo } = useContext(AuthContext);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentValidationID, setCurrentValidationID] = useState(0);
    const [conditionToEdit, setConditionToEdit] = useState<SectionCondition>()
    const selectedSection = appContext?.questionnaire?.SelectedSection;
    const questionIDField = watch('QuestionID');
    const actionTypeField = watch('ActionType');
    let conditionType: ConditionType[];
    let options: QuestionOption[];
    let optionLength: number = 0;
    let actionType: Action;
    conditionType = [];
    options = [];
    actionType = {} as Action;
    const conditionsByType = (uiType: number | undefined) => {
        if (!uiType) {
            return [{
                Value: 'Empty',
                Description: 'Empty'
            },
            {
                Value: 'Filled',
                Description: 'Filled'
            },
            ]
        }
        if ([UIElementEnum.date, UIElementEnum.datetime].includes(uiType)) {
            return [{
                Value: 'Empty',
                Description: 'Empty'
            },
            {
                Value: 'Filled',
                Description: 'Filled'
            },
            {
                Value: 'Before',
                Description: 'Before'
            },
            {
                Value: 'After',
                Description: 'After'
            },
            {
                Value: 'EqualTo',
                Description: 'Equal to'
            },
            {
                Value: 'NotEqualTo',
                Description: 'Not Equal to'
            }
            ]
        }
        else if ([UIElementEnum.ta, UIElementEnum.tb].includes(uiType)) {
            return [{
                Value: 'Empty',
                Description: 'Empty'
            },
            {
                Value: 'Filled',
                Description: 'Filled'
            }
            ]

        }
        else if ([UIElementEnum.cb].includes(uiType)) {
            return [{
                Value: 'Empty',
                Description: 'Not Selected'
            },
            {
                Value: 'Filled',
                Description: 'Selected'
            },
            ]
        }
        else if ([UIElementEnum.rb, UIElementEnum.rbBlock, UIElementEnum.rbil].includes(uiType)) {
            return [{
                Value: 'Empty',
                Description: 'Empty'
            },
            {
                Value: 'Filled',
                Description: 'Filled'
            },
            {
                Value: 'EqualTo',
                Description: 'Equal to'
            },
            {
                Value: 'NotEqualTo',
                Description: 'Not Equal to'
            }
            ]
        }
        else {
            return [{
                Value: 'Empty',
                Description: 'Empty'
            },
            {
                Value: 'Filled',
                Description: 'Filled'
            },
            ]
        }
    }
    if (actionTypeField) {
        actionType = actionTypes.find(f => f.Value === actionTypeField)!;
    }

    if (questionIDField) {
        const selectedQuestion = selectedSection?.Questions?.find(f => f.QuestionID === questionIDField)!;
        options = selectedQuestion?.QuestionOptions
        optionLength = options?.length || 0;
        conditionType = conditionsByType(selectedQuestion?.UIElementID);
    }


    const getQuestionLabel = (questionID: number) => {
        return selectedSection.Questions.find(f => f.QuestionID === questionID)?.Label
    }

    const getActionTypeLabel = (actionTypeValue: string) => {
        return actionTypes.find(f => f.Value === actionTypeValue)?.Description
    }

    const getActionField = (id: number, actionTypeValue: string) => {
        const action = actionTypes.find(f => f.Value === actionTypeValue);
        switch (action?.Type){
            case 'Question':
                return selectedSection.Questions.find(f => f.QuestionID === id)?.Label || '';
            case 'Section':
                return appContext?.questionnaire?.PageSelected?.Sections?.find(f => f.SectionID === id)?.Label || ''
            case 'Page':
                return appContext?.questionnaire?.PhqSelected?.Pages!.find(p => p.PageID === id)?.Name || '';
            default:
                return null;
        }
    }

    const getConditionValue = (value: any, questionID: number) => {
        const question = selectedSection.Questions.find(f => f.QuestionID === questionID);
        if (question?.QuestionOptions?.length! > 0) {
            return question?.QuestionOptions.find(f => f.Name === value)?.Name || '';
        }
        else {
            return value;
        }
    }

    const createPayloadValidation = (payload: SectionCondition[]) => {
        const newValidation = new Validation();
        newValidation.AppliesTo = ValidationAppliesToEnum.section;
        newValidation.CreatedBy = logInfo.UserID;
        newValidation.AppliesToID = selectedSection.SectionID;
        newValidation.ValidationTypeID = ValidationTypeEnum.Condition;
        newValidation.ValidationLogic = JSON.stringify(payload);
        return newValidation;
    }
    const updateContext = () => {
        const reducerPayload = {
            sectionID: selectedSection.SectionID,
            value: selectedSection
        }
        appContext.updateQuestionnaire(QuestionnaireActionTypes.UPDATE_SECTION, reducerPayload);
    }
    const updateValidations = () => {
        const updatePayload = new ValidationToUpdate();
        updatePayload.ValidationLogic = JSON.stringify(selectedSection.Conditions);
        updatePayload.ValidationTypeID = ValidationTypeEnum.Condition;
        updatePayload.ValidationID = currentValidationID;
        updateValidation(updatePayload);
    }
    const addConditionToTheList = async (data: any) => {
        let payload;
        const formData = data as SectionCondition;
        if (isEditMode) {
            const indexConditionToUpdate = selectedSection.Conditions?.findIndex(c => c.ConditionID === conditionToEdit?.ConditionID)
            selectedSection.Conditions![indexConditionToUpdate!] = formData;
            // update it 
            updateValidations();
        }
        else {
            formData.Priority = selectedSection.Conditions?.length! + 1 || 10;
            payload = JSON.parse(JSON.stringify(selectedSection))

            const currentConditions = selectedSection.Conditions || [];
            if (typeof (currentConditions) !== "string") {
                currentConditions?.push(formData)
            }
            payload.Conditions = JSON.stringify(currentConditions);
            selectedSection.Conditions = currentConditions;
            // create new validation
            if (currentValidationID) {
                updateValidations();
            }
            else {
                createNewValidation(createPayloadValidation(currentConditions)).then(f => {
                    if (f.data?.result?.ValidationID) {
                        setCurrentValidationID(f.data.result.ValidationID)
                    }
                })
            }


        }
        updateContext();
        if (isEditMode) {
            appContext.notifySuccess('Condition updated!');
        }
        else {
            appContext.notifySuccess('Condition added!');
        }
        setIsEditMode(false);
        reset(initialValues);
    }

    const editCondition = (condition: SectionCondition) => {
        setIsEditMode(true);
        setConditionToEdit(condition)
        reset(condition)
    }
    const deleteCondition = (index: number) => {
        selectedSection.Conditions?.splice(index, 1);
        updateValidations();
        updateContext();
    }
    const fixHeights = () => {
        setTimeout(() => {
            const buttons = document.querySelectorAll('.alert-radio-button');
            buttons.forEach((btn: any) => {
                btn.style.height = (btn.scrollHeight) + "px";
            });
        }, 0)
    }


    useEffect(() => {
        const params = {
            AppliesTo: ValidationAppliesToEnum.section,
            AppliesToID: selectedSection.SectionID,
            ValidationTypeID: ValidationTypeEnum.Condition
        } as ValidationSearchParams;
        getValidationList(params).then(f => {
            if (f.data?.result?.length > 0) {
                selectedSection.Conditions = JSON.parse(f.data.result[0].ValidationLogic);
                updateContext();
                setCurrentValidationID(f.data.result[0].ValidationID)
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <IonRow>

            <IonCol size='6'>
                <form onSubmit={handleSubmit(data => addConditionToTheList(data))}>
                    <IonGrid>
                        <IonRow>
                            <IonCol>
                                <IonCard>
                                    <IonCardContent>
                                        <IonItem className="ion-text-wrap" lines="inset">

                                            <IonLabel className='input-title' position="stacked">Question</IonLabel>
                                            <Controller
                                                render={({ onChange, onBlur, value, name }) => (
                                                    <IonSelect  interface="popover" placeholder="Select One" value={value} name={name} onIonChange={(e) => { onChange(e.detail.value) }}>
                                                        {selectedSection?.Questions?.filter(f => f.UIElementID !== UIElementEnum.pa).map((q) =>
                                                            <IonSelectOption className="ion-text-wrap" key={q.QuestionID} value={q.QuestionID}>{q.Label}</IonSelectOption>
                                                        )}
                                                    </IonSelect>
                                                )}
                                                control={control}
                                                name="QuestionID"
                                                rules={{ required: true }}

                                            />
                                        </IonItem>
                                        <IonItem disabled={!questionIDField} className="ion-text-wrap" lines="inset">
                                            <IonLabel className='input-title' position="stacked">Condition</IonLabel>
                                            <Controller
                                                render={({ onChange, onBlur, value, name }) => (
                                                    <IonSelect  interface="popover" placeholder="Select One" value={value} name={name} onIonChange={(e) => { onChange(e.detail.value) }} >
                                                        {conditionType?.map((c) =>
                                                            <IonSelectOption className="ion-text-wrap" key={c.Value} value={c.Value}>{c.Description}</IonSelectOption>
                                                        )}
                                                    </IonSelect>
                                                )}
                                                control={control}
                                                name="ConditionType"

                                            />
                                        </IonItem>
                                        <IonItem disabled={!optionLength} className="ion-text-wrap" lines="inset">
                                            <IonLabel className='input-title' position="stacked">Value</IonLabel>
                                            <Controller
                                                render={({ onChange, onBlur, value, name }) => (
                                                    <IonSelect placeholder="Select One" value={value} name={name} onIonChange={(e) => { onChange(e.detail.value) }}>
                                                        {options?.map((c) =>
                                                            <IonSelectOption className="ion-text-wrap" key={c.Name} value={c.Name}>{c.Label}</IonSelectOption>
                                                        )}
                                                    </IonSelect>
                                                )}
                                                control={control}
                                                name="ConditionValue"
                                            />
                                        </IonItem>

                                    </IonCardContent>

                                </IonCard>
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol>
                                <IonCard>
                                    <IonCardHeader> Actions</IonCardHeader>
                                    <IonCardContent>
                                        <IonItem className="ion-text-wrap" lines="inset">
                                            <IonLabel className='input-title' position="stacked">Do</IonLabel>
                                            <Controller
                                                render={({ onChange, onBlur, value, name }) => (
                                                    <IonSelect placeholder="Select One" value={value} name={name} onIonChange={(e) => { onChange(e.detail.value) }}>
                                                        {actionTypes?.map((c) =>
                                                            <IonSelectOption className="ion-text-wrap" key={c.Value} value={c.Value}>{c.Description}</IonSelectOption>
                                                        )}
                                                    </IonSelect>
                                                )}
                                                control={control}
                                                name="ActionType"
                                                id="ActionType"
                                            />
                                        </IonItem>
                                        <IonItem disabled={!actionTypeField} className="ion-text-wrap" lines="inset">
                                            <IonLabel className='input-title' position="stacked">Field</IonLabel>
                                            <Controller
                                                render={({ onChange, onBlur, value, name }) => (
                                                    <IonSelect placeholder="Select One" value={value} name={name} onIonChange={(e) => { onChange(e.detail.value) }} onIonBlur={() => fixHeights()}>
                                                        {
                                                            actionType?.Type === 'Question' && (
                                                                selectedSection?.Questions?.filter(f => f.UIElementID !== UIElementEnum.pa).map((q) =>
                                                                    <IonSelectOption className="ion-text-wrap" key={q.QuestionID} value={q.QuestionID}>{q.Label}</IonSelectOption>
                                                                )
                                                            )
                                                        }
                                                        {
                                                            actionType?.Type === 'Section' && (
                                                                appContext?.questionnaire?.PageSelected.Sections?.map((s, index) =>
                                                                    <IonSelectOption className="ion-text-wrap" key={index} value={s.SectionID}>{s.Label}</IonSelectOption>
                                                                )
                                                            )
                                                        }
                                                        {
                                                            actionType?.Type === 'Page' && (
                                                                appContext?.questionnaire?.PhqSelected?.Pages!.map((p, index) => 
                                                                    <IonSelectOption className="ion-text-wrap" key={index} value={p.PageID}>{p.Name}</IonSelectOption>
                                                                )
                                                            )
                                                        }
                                                    </IonSelect>
                                                )}
                                                control={control}
                                                name="ActionFieldID"
                                                id="ActionFieldID"
                                            />
                                        </IonItem>
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol>
                                <IonToolbar class="ion-no-border">
                                    <IonButtons slot="end">
                                        <IonButton
                                            type="button"
                                            onClick={() => {
                                                reset(initialValues);
                                            }}
                                        >
                                            Reset
                                     </IonButton>
                                        <IonButton fill="solid" type="submit" >
                                            Save
                                         </IonButton>
                                    </IonButtons>
                                </IonToolbar>

                            </IonCol>
                        </IonRow>
                    </IonGrid>
                </form >
            </IonCol>


            <IonCol size='6'>
                {selectedSection?.Conditions?.map((condition, index) => {
                    return (
                        <IonCard key={index}>
                            <IonCardContent>
                                <IonToolbar>
                                    <p><IonText color="primary"><strong>If </strong></IonText> {getQuestionLabel(condition.QuestionID)}  <IonText color="primary"><strong> is </strong></IonText> {condition.ConditionType}
                                        {condition.ConditionValue && (
                                            <React.Fragment>
                                                <IonText color="primary"><strong> with </strong></IonText> {getConditionValue(condition.ConditionValue, condition.QuestionID)}
                                            </React.Fragment>)}
                                    </p>
                                    <p><IonText color="primary"><strong>Do </strong></IonText>  {getActionTypeLabel(condition.ActionType)} <IonText color="primary"><strong> on </strong></IonText> {getActionField(condition.ActionFieldID, condition.ActionType)}</p>
                                    <IonButtons slot="end">
                                        <IonButton size="small" color="primary" onClick={() => { editCondition(condition) }}>
                                            <IonIcon slot="icon-only" icon={createOutline} />
                                        </IonButton>
                                        <IonButton size="small" color="danger" onClick={() => { deleteCondition(index) }}>
                                            <IonIcon slot="icon-only" icon={trashOutline} />
                                        </IonButton>
                                    </IonButtons>
                                </IonToolbar>
                            </IonCardContent>
                        </IonCard>
                    )

                })}
            </IonCol>
        </IonRow>

    );
};

export default ConditionalForm;
